<template>
  <div>
    <v-card :disabled="isProcessing" :loading="isProcessing">
      <template v-slot:progress>
        <v-progress-linear absolute color="success" height="4" indeterminate></v-progress-linear>
      </template>
      <v-tabs v-model="tab" class="stock-color" left dark icons-and-text>
        <v-tabs-slider></v-tabs-slider>
        <v-tab href="#tab-1">
          <v-toolbar-title>{{ title }}</v-toolbar-title>
        </v-tab>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item value="tab-1">
          <v-card flat>
            <v-card-text>
              <v-form :key="formKey" ref="requestData" v-model="validRequestData" @submit.prevent>
                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="requestData.display_name"
                      :label="$t('STOCK.PRODUCTS.DISPLAY_NAME') + '*'"
                      :rules="[validations.required()]"
                      :error-messages="validationErrors.display_name || []"
                      @change="setChange('display_name')"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="requestData.display_name_bg"
                      :label="$t('STOCK.PRODUCTS.DISPLAY_NAME_BG')"
                      :error-messages="validationErrors.display_name_bg || []"
                      @change="setChange('display_name_bg')"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="requestData.code"
                      :label="$t('STOCK.PRODUCTS.CODE')"
                      :error-messages="validationErrors.code || []"
                      @change="setChange('code')"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      value="kg"
                      :label="$t('STOCK.PRODUCTS.PRIMARY_UNIT')"
                      disabled
                      readonly
                      hint="Always kg"
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-checkbox
                      v-model="requestData.fixed_weight"
                      class="mt-0 mt-md-5"
                      :label="$t('STOCK.PRODUCTS.FIXED_WEIGHT')"
                      color="primary"
                      hide-details
                      @change="setChange('fixed_weight')"
                    ></v-checkbox>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="4">
                    <v-checkbox
                      v-model="requestData.has_secondary_unit"
                      class="mt-0 mt-md-5"
                      :label="$t('STOCK.PRODUCTS.HAS_SECONDARY_UNIT')"
                      color="primary"
                      hide-details
                      @change="handleSecondaryUnitToggle"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-autocomplete
                      v-model="requestData.secondary_unit_id"
                      :items="secondaryUnitOptions"
                      item-text="label"
                      item-value="id"
                      :disabled="!requestData.has_secondary_unit"
                      :label="$t('STOCK.PRODUCTS.SECONDARY_UNIT')"
                      :error-messages="validationErrors.secondary_unit_id || []"
                      clearable
                      @change="setChange('secondary_unit_id')"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model.number="requestData.secondary_unit_kg"
                      type="number"
                      min="0"
                      step="any"
                      :disabled="!requestData.has_secondary_unit"
                      :label="$t('STOCK.PRODUCTS.SECONDARY_UNIT_KG')"
                      :error-messages="validationErrors.secondary_unit_kg || []"
                      @change="setChange('secondary_unit_kg')"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    <div class="d-flex align-center mb-2">
                      <div class="subtitle-2 mb-0">{{ $t("STOCK.PRODUCTS.PACKAGING_LEVELS") }}</div>
                      <v-spacer></v-spacer>
                      <v-btn
                        small
                        color="primary"
                        :disabled="requestData.packaging_levels.length >= 3"
                        @click="addPackagingLevel"
                      >
                        {{ $t("STOCK.PRODUCTS.ADD_PACKAGING_LEVEL") }}
                      </v-btn>
                    </div>
                    <v-row
                      v-for="(level, index) in requestData.packaging_levels"
                      :key="'packaging-level-' + level.level"
                      class="align-center"
                    >
                      <v-col cols="12" md="1">
                        <div class="caption">
                          {{ $t("STOCK.PRODUCTS.PACKAGING_LEVEL", { level: level.level }) }}
                        </div>
                      </v-col>
                      <v-col cols="12" md="5">
                        <v-autocomplete
                          v-model="level.packaging_id"
                          :items="packagings"
                          item-text="label"
                          item-value="id"
                          :label="$t('STOCK.PRODUCTS.PACKAGING')"
                          :error-messages="
                            (validationErrors[`packaging_levels.${index}.packaging_id`] ||
                              validationErrors.packaging_levels ||
                              [])
                          "
                          @change="setChange('packaging_levels')"
                        ></v-autocomplete>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model.number="level.weight_kg"
                          type="number"
                          min="0"
                          step="any"
                          :label="$t('STOCK.PRODUCTS.PACKAGING_WEIGHT_KG')"
                          :error-messages="
                            validationErrors[`packaging_levels.${index}.weight_kg`] || []
                          "
                          @change="setChange('packaging_levels')"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="2" class="text-right">
                        <v-btn icon color="error" @click="removePackagingLevel(index)">
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model.number="requestData.shelf_life_days"
                      type="number"
                      min="0"
                      :label="$t('STOCK.PRODUCTS.SHELF_LIFE_DAYS')"
                      :error-messages="validationErrors.shelf_life_days || []"
                      @change="setChange('shelf_life_days')"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-autocomplete
                      v-model="requestData.origin_country_id"
                      :items="countries"
                      item-text="label"
                      item-value="id"
                      :label="$t('STOCK.PRODUCTS.ORIGIN_COUNTRY')"
                      :error-messages="validationErrors.origin_country_id || []"
                      clearable
                      @change="setChange('origin_country_id')"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="requestData.veterinary_number"
                      :label="$t('STOCK.PRODUCTS.VETERINARY_NUMBER')"
                      :error-messages="validationErrors.veterinary_number || []"
                      @change="setChange('veterinary_number')"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="requestData.producer_name"
                      :label="$t('STOCK.PRODUCTS.PRODUCER_NAME')"
                      :error-messages="validationErrors.producer_name || []"
                      @change="setChange('producer_name')"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-checkbox
                      v-model="requestData.is_active"
                      class="mt-0 mt-md-5"
                      :label="$t('GENERAL.LABELS.ACTIVE')"
                      color="primary"
                      hide-details
                      @change="setChange('is_active')"
                    ></v-checkbox>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="4">
                    <v-autocomplete
                      v-model="requestData.temperature_id"
                      :items="temperatures"
                      item-text="label"
                      item-value="id"
                      :label="$t('STOCK.PRODUCTS.TEMPERATURE')"
                      :error-messages="validationErrors.temperature_id || []"
                      clearable
                      @change="setChange('temperature_id')"
                    ></v-autocomplete>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="6">
                    <StockProductDepartmentPicker
                      v-model="requestData.stock_product_department_id"
                      :initial-department="selectedStockProductDepartment"
                      :label="$t('STOCK.PRODUCT_CATEGORIES.DEPARTMENT') + '*'"
                      :error-messages="validationErrors.stock_product_department_id || []"
                      @change="handleDepartmentChange"
                    ></StockProductDepartmentPicker>
                  </v-col>
                  <v-col cols="12" md="6">
                    <StockProductCategoryPicker
                      v-model="requestData.stock_product_category_id"
                      :initial-category="selectedStockProductCategory"
                      :department-id="requestData.stock_product_department_id"
                      :disabled="!requestData.stock_product_department_id"
                      :error-messages="validationErrors.stock_product_category_id || []"
                      @change="handleCategoryChange"
                    ></StockProductCategoryPicker>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      v-model="requestData.description_mapping_value_id"
                      :items="filteredDescriptionOptions"
                      item-text="label"
                      item-value="id"
                      :label="$t('STOCK.PRODUCTS.DESCRIPTION_MAPPING')"
                      :loading="isLoadingDescriptions"
                      :disabled="!descriptionMappingEnabled && !requestData.description_mapping_value_id"
                      :hint="
                        descriptionMappingEnabled
                          ? ''
                          : $t('STOCK.PRODUCTS.DESCRIPTION_MAPPING_REQUIRES_CATEGORY_MEAT')
                      "
                      :persistent-hint="!descriptionMappingEnabled"
                      :error-messages="validationErrors.description_mapping_value_id || []"
                      clearable
                      @change="setChange('description_mapping_value_id')"
                    ></v-autocomplete>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    <StockTagPicker
                      v-model="requestData.stock_tag_ids"
                      :category-id="requestData.stock_product_category_id"
                      :display-name="requestData.display_name"
                      :initial-tags="selectedStockTags"
                      :error-messages="validationErrors.stock_tag_ids || []"
                      @change="setChange('stock_tag_ids')"
                    ></StockTagPicker>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-btn
                v-if="isEditing && hasPermissions('stock-products-create')"
                color="primary"
                outlined
                @click="createSimilar"
              >
                {{ $t("STOCK.PRODUCTS.CREATE_SIMILAR") }}
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn color="warning" @click="back(true)">
                {{ $t("GENERAL.LABELS.BACK_BUTTON") }}
              </v-btn>
              <v-btn color="success" @click="store">
                {{ actionText }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
    <TPConfirm
      v-model="showConfirmSet"
      content="All current changes will be lost."
      @confirm="back"
    ></TPConfirm>
  </div>
</template>

<script>
import validations from "@/views/partials/form/Validations.vue";
import TPConfirm from "@/views/partials/dialogs/Confirm.vue";
import StockTagPicker from "@/views/partials/stock/StockTagPicker.vue";
import StockProductDepartmentPicker from "@/views/partials/stock/StockProductDepartmentPicker.vue";
import StockProductCategoryPicker from "@/views/partials/stock/StockProductCategoryPicker.vue";
import userStateService from "@/common/userState.service";
import { GET_COUNTRIES } from "@/store/countries.module";
import { SHOW_SNACK } from "@/store/snack.module";
import { GET_DESCRIPTIONS } from "@/store/mappingValues.module";
import {
  CREATE_STOCK_PRODUCT,
  GET_STOCK_PRODUCT,
  GET_STOCK_PRODUCT_UNITS,
  GET_STOCK_PACKAGING_OPTIONS,
  GET_STOCK_TEMPERATURE_OPTIONS,
  UPDATE_STOCK_PRODUCT,
} from "@/store/stock/stockProducts.module";

export default {
  name: "StockEditProduct",
  components: {
    TPConfirm,
    StockTagPicker,
    StockProductDepartmentPicker,
    StockProductCategoryPicker,
  },
  data() {
    return {
      validations,
      tab: "tab-1",
      formKey: "",
      isProcessing: false,
      isLoadingDescriptions: false,
      validRequestData: false,
      hasChanges: false,
      showConfirmSet: false,
      validationErrors: {},
      title: this.$t("STOCK.PRODUCTS.CREATE"),
      actionText: this.$t("GENERAL.LABELS.CREATE_BUTTON"),
      units: [],
      temperatures: [],
      packagings: [],
      countries: [],
      descriptionOptions: [],
      selectedStockTags: [],
      selectedStockProductCategory: null,
      selectedStockProductDepartment: null,
      selectedDescriptionMappingValue: null,
      requestData: {
        code: "",
        display_name: "",
        display_name_bg: "",
        stock_product_department_id: null,
        stock_product_category_id: null,
        description_mapping_value_id: null,
        temperature_id: null,
        fixed_weight: true,
        has_secondary_unit: false,
        secondary_unit_id: null,
        secondary_unit_kg: null,
        packaging_levels: [],
        shelf_life_days: null,
        origin_country_id: null,
        veterinary_number: "",
        producer_name: "",
        is_active: true,
        stock_tag_ids: [],
      },
    };
  },
  computed: {
    secondaryUnitOptions() {
      return (this.units || []).filter((unit) => String(unit.code || "").toLowerCase() !== "kg");
    },
    isEditing() {
      return !!this.$route.params.id;
    },
    copySourceId() {
      if (this.isEditing) {
        return null;
      }

      const copy = this.$route.query.copy;
      return copy ? String(copy) : null;
    },
    descriptionMappingEnabled() {
      return !!this.selectedStockProductCategory?.meat_mapping_value_id;
    },
    filteredDescriptionOptions() {
      const meatMappingValueId = this.selectedStockProductCategory?.meat_mapping_value_id;
      let options = meatMappingValueId
        ? this.descriptionOptions.filter(
            (item) => Number(item.group_value_id) === Number(meatMappingValueId)
          )
        : [];

      const selectedId = this.requestData.description_mapping_value_id;
      if (
        selectedId &&
        !options.some((item) => Number(item.id) === Number(selectedId))
      ) {
        const selected =
          this.descriptionOptions.find((item) => Number(item.id) === Number(selectedId)) ||
          (this.selectedDescriptionMappingValue
            ? {
                id: this.selectedDescriptionMappingValue.id,
                group_value_id: this.selectedDescriptionMappingValue.group_value_id || null,
                label:
                  this.selectedDescriptionMappingValue.label ||
                  this.selectedDescriptionMappingValue.value ||
                  `Value #${this.selectedDescriptionMappingValue.id}`,
              }
            : null);

        if (selected) {
          options = [selected, ...options];
        }
      }

      return options;
    },
  },
  mounted() {
    this.bootstrap();
  },
  watch: {
    "$route.fullPath"() {
      this.bootstrap();
    },
  },
  methods: {
    loadUnits() {
      return this.$store
        .dispatch(GET_STOCK_PRODUCT_UNITS)
        .then((response) => {
          this.units = response.data || [];
        });
    },
    loadTemperatures() {
      return this.$store
        .dispatch(GET_STOCK_TEMPERATURE_OPTIONS)
        .then((response) => {
          this.temperatures = response.data || [];
        });
    },
    loadPackagings() {
      return this.$store
        .dispatch(GET_STOCK_PACKAGING_OPTIONS)
        .then((response) => {
          this.packagings = response.data || [];
        });
    },
    loadCountries() {
      return this.$store
        .dispatch(GET_COUNTRIES)
        .then((response) => {
          this.countries = Array.isArray(response) ? response : (response.data || []);
        })
        .catch(() => {
          this.countries = [];
        });
    },
    loadDescriptions() {
      this.isLoadingDescriptions = true;
      return this.$store
        .dispatch(GET_DESCRIPTIONS)
        .then((response) => {
          const descriptions = Array.isArray(response) ? response : [];
          const parentIds = new Set(
            descriptions
              .filter((item) => item.parent_id != null)
              .map((item) => Number(item.parent_id))
          );

          this.descriptionOptions = descriptions
            .filter((item) => !parentIds.has(Number(item.id)))
            .map((item) => ({
              id: item.id,
              group_value_id: item.group_value_id || null,
              label: item.value || `Value #${item.id}`,
            }));
        })
        .catch(() => {
          this.descriptionOptions = [];
        })
        .finally(() => {
          this.isLoadingDescriptions = false;
        });
    },
    emptyRequestData() {
      return {
        code: "",
        display_name: "",
        display_name_bg: "",
        stock_product_department_id: null,
        stock_product_category_id: null,
        description_mapping_value_id: null,
        temperature_id: null,
        fixed_weight: true,
        has_secondary_unit: false,
        secondary_unit_id: null,
        secondary_unit_kg: null,
        packaging_levels: [],
        shelf_life_days: null,
        origin_country_id: null,
        veterinary_number: "",
        producer_name: "",
        is_active: true,
        stock_tag_ids: [],
      };
    },
    bootstrap() {
      this.loadToken = (this.loadToken || 0) + 1;
      const loadToken = this.loadToken;
      this.formKey = this.$route.fullPath;
      this.isProcessing = true;
      this.validationErrors = {};
      this.hasChanges = false;
      this.title = this.$t("STOCK.PRODUCTS.CREATE");
      this.actionText = this.$t("GENERAL.LABELS.CREATE_BUTTON");
      this.selectedStockTags = [];
      this.selectedStockProductCategory = null;
      this.selectedStockProductDepartment = null;
      this.selectedDescriptionMappingValue = null;
      this.requestData = this.emptyRequestData();

      Promise.all([
        this.loadUnits(),
        this.loadTemperatures(),
        this.loadPackagings(),
        this.loadCountries(),
        this.loadDescriptions(),
      ])
        .then(() => this.selectProduct(loadToken))
        .finally(() => {
          if (this.loadToken === loadToken) {
            this.isProcessing = false;
          }
        });
    },
    selectProduct(loadToken) {
      const productId = this.$route.params.id || this.copySourceId;
      if (!productId) {
        return;
      }

      if (this.isEditing) {
        this.title = this.$t("STOCK.PRODUCTS.EDIT");
        this.actionText = this.$t("GENERAL.LABELS.SAVE");
      }

      return this.$store
        .dispatch(GET_STOCK_PRODUCT, productId)
        .then((response) => {
          if (this.loadToken !== loadToken) {
            return;
          }
          this.fillFromProduct(response, !!this.copySourceId);
        });
    },
    fillFromProduct(response, asCopy) {
      const displayName = response.display_name || "";
      const displayNameBg = response.display_name_bg || "";

      this.selectedStockTags = response.stock_tags || [];
      this.selectedStockProductCategory = response.stock_product_category || null;
      this.selectedStockProductDepartment = response.stock_product_department || null;
      this.selectedDescriptionMappingValue = response.description_mapping_value || null;
      this.requestData = {
        code: response.code || "",
        display_name: asCopy ? this.withCopySuffix(displayName) : displayName,
        display_name_bg: asCopy && displayNameBg ? this.withCopySuffix(displayNameBg) : displayNameBg,
        stock_product_department_id: response.stock_product_department?.id || null,
        stock_product_category_id: response.stock_product_category_id,
        description_mapping_value_id: response.description_mapping_value_id || null,
        temperature_id: response.temperature_id,
        fixed_weight: response.fixed_weight !== false,
        has_secondary_unit: !!response.has_secondary_unit,
        secondary_unit_id: response.secondary_unit_id,
        secondary_unit_kg: response.secondary_unit_kg,
        packaging_levels: (response.packaging_levels || []).map((level) => ({
          level: level.level,
          packaging_id: level.packaging_id,
          weight_kg: level.weight_kg,
        })),
        shelf_life_days: response.shelf_life_days,
        origin_country_id: response.origin_country_id,
        veterinary_number: response.veterinary_number || "",
        producer_name: response.producer_name || "",
        is_active: !!response.is_active,
        stock_tag_ids: response.stock_tag_ids || [],
      };
    },
    withCopySuffix(name) {
      const suffix = " - Copy";
      const trimmed = String(name || "").trim();
      if (!trimmed) {
        return "Copy";
      }
      if (trimmed.length + suffix.length <= 255) {
        return `${trimmed}${suffix}`;
      }
      return `${trimmed.slice(0, 255 - suffix.length).trim()}${suffix}`;
    },
    createSimilar() {
      this.hasChanges = false;
      this.$router.push({
        name: "stock-products-create",
        query: { copy: String(this.$route.params.id) },
      });
    },
    getErrorMessage(error) {
      if (error && error.errors && typeof error.errors === "object") {
        const firstErrorField = Object.keys(error.errors)[0];
        const firstErrorList = error.errors[firstErrorField];
        if (Array.isArray(firstErrorList) && firstErrorList.length) {
          return firstErrorList[0];
        }
      }
      return error?.message || this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    setValidationErrors(error) {
      this.validationErrors = (error && error.errors) || {};
    },
    clearValidationError(field) {
      if (this.validationErrors[field]) {
        this.$delete(this.validationErrors, field);
      }
    },
    setChange(field) {
      this.hasChanges = true;
      this.clearValidationError(field);
    },
    handleSecondaryUnitToggle() {
      this.hasChanges = true;
      this.clearValidationError("has_secondary_unit");
      this.clearValidationError("secondary_unit_id");
      this.clearValidationError("secondary_unit_kg");
      if (!this.requestData.has_secondary_unit) {
        this.requestData.secondary_unit_id = null;
        this.requestData.secondary_unit_kg = null;
      }
    },
    addPackagingLevel() {
      if (this.requestData.packaging_levels.length >= 3) {
        return;
      }
      this.requestData.packaging_levels.push({
        level: this.requestData.packaging_levels.length + 1,
        packaging_id: null,
        weight_kg: null,
      });
      this.setChange("packaging_levels");
    },
    removePackagingLevel(index) {
      this.requestData.packaging_levels.splice(index, 1);
      this.requestData.packaging_levels = this.requestData.packaging_levels.map((level, levelIndex) => ({
        ...level,
        level: levelIndex + 1,
      }));
      this.setChange("packaging_levels");
    },
    handleDepartmentChange() {
      this.setChange("stock_product_department_id");
      this.requestData.stock_product_category_id = null;
      this.selectedStockProductCategory = null;
      this.clearValidationError("stock_product_category_id");
    },
    handleCategoryChange(category) {
      this.setChange("stock_product_category_id");
      this.selectedStockProductCategory = category || null;
    },
    buildSavePayload() {
      const {
        stock_product_department_id: _departmentId,
        packaging_levels,
        has_secondary_unit,
        ...params
      } = this.requestData;

      return {
        ...params,
        has_secondary_unit: !!has_secondary_unit,
        secondary_unit_id: has_secondary_unit ? params.secondary_unit_id : null,
        secondary_unit_kg: has_secondary_unit ? params.secondary_unit_kg : null,
        packaging_levels: (packaging_levels || [])
          .filter((level) => level.packaging_id && level.weight_kg)
          .map((level, index) => ({
            level: index + 1,
            packaging_id: level.packaging_id,
            weight_kg: level.weight_kg,
          })),
      };
    },
    back(check) {
      if (check && this.hasChanges) {
        this.showConfirmSet = true;
        return;
      }
      this.hasChanges = false;
      this.$router.push({ name: "stock-products" });
    },
    store() {
      this.$refs.requestData.validate();
      if (!this.validRequestData) {
        return;
      }

      this.validationErrors = {};

      if (!this.requestData.stock_product_department_id) {
        this.validationErrors.stock_product_department_id = [
          this.$t("GENERAL.VALIDATION.REQUIRED_FIELD"),
        ];
        return;
      }

      if (!this.requestData.stock_product_category_id) {
        this.validationErrors.stock_product_category_id = [
          this.$t("GENERAL.VALIDATION.REQUIRED_FIELD"),
        ];
        return;
      }

      let action = CREATE_STOCK_PRODUCT;
      const params = this.buildSavePayload();
      let payload = params;
      if (this.$route.params.id) {
        action = UPDATE_STOCK_PRODUCT;
        payload = {
          id: this.$route.params.id,
          params,
        };
      }

      this.isProcessing = true;
      this.$store
        .dispatch(action, payload)
        .then((response) => {
          const blinkId = response && response.id ? response.id : this.$route.params.id;
          if (blinkId) {
            userStateService.setState("table-state.stock-products-table.blinkRowItemKey", blinkId);
          }
          this.$store.dispatch(SHOW_SNACK, { message: this.$t("GENERAL.SUCCESS_MESSAGE") });
          this.hasChanges = false;
          this.$router.push({ name: "stock-products" });
        })
        .catch((error) => {
          this.setValidationErrors(error);
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },
  },
};

</script>
